import { AppLayout } from '@/components/layouts/AppLayout'
import { FindingItem, PositionFeedbackTable } from '@/components/organisms/PositionFeedbackTable'
import { isOpen, isShown } from '@/utils/findings'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { queryClient } from '@/integrations/react-query'
import { fetchEstimateFeedbackById, FindingResponseInput, respondToFinding } from '@/services/cases'
import type { EstimateFeedback as TEstimateFeedback, FindingView } from '@backend/schemas'
import { useMutation, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ArrowLeft, Car } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

type Damage = NonNullable<TEstimateFeedback['damages']>[number]

const DAMAGE_KIND_LABELS: Record<Damage['kind'], string> = {
  change: 'byte',
  usedPart: 'byte, begagnad del',
  ytrikta: 'ytrikta',
  plasticRepair: 'plastreparation',
  paintOnly: 'endast lack',
}

const TabLabel = ({ label, findings }: { label: string; findings: FindingView[] }) => {
  const open = findings.filter(isOpen).length
  return (
    <span className="flex items-center gap-2">
      {label}
      {open > 0 && <Badge variant="secondary">{open}</Badge>}
    </span>
  )
}

const EstimateFeedback = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: estimateFeedback, isLoading: isFetching } = useQuery({
    queryKey: ['feedback', id],
    queryFn: () => fetchEstimateFeedbackById(id),
  })

  const {
    mutate: respond,
    isPending,
    variables,
  } = useMutation({
    mutationKey: ['respond-feedback', id],
    mutationFn: (input: FindingResponseInput) => respondToFinding(id, input),
    onSuccess(data) {
      queryClient.setQueryData(['feedback', id], data)
    },
  })

  if (isFetching) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64" />
        </div>
      </AppLayout>
    )
  }

  if (!estimateFeedback) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Estimat hittades inte</p>
        </div>
      </AppLayout>
    )
  }

  const positions = estimateFeedback.positions ?? []
  const findings = estimateFeedback.findings ?? []
  const responses = estimateFeedback.responses ?? {}
  const summary = estimateFeedback.summary
  const respondProps = { responses, pendingKey: isPending ? variables?.key : undefined, onRespond: respond }

  const positionFindings = findings.filter(finding => finding.position !== null)
  const estimateFindings = findings.filter(finding => finding.position === null && isShown(finding, responses))
  const otherFindings = estimateFindings.filter(finding => finding.column !== 'paint')
  const otherPaintFindings = estimateFindings.filter(finding => finding.column === 'paint')
  const positionTitle = (nr: number) => positions.find(position => position.position === nr)?.title ?? `Position ${nr}`

  return (
    <AppLayout>
      <div className="space-y-6 static gap-4">
        <div className="sticky top-0 z-10 -m-6 bg-card shadow-sm mb-1">
          <div className="flex items-center justify-between gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              <p>Tillbaka</p>
            </Button>
          </div>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Car className="h-4 w-4" />
              {estimateFeedback.regNr}-{estimateFeedback.cabasCaseIndex}
            </CardTitle>
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="shrink-0">Kalkyl-ID: {estimateFeedback.cabasCalculationId ?? estimateFeedback.calculationId}</span>
              </div>
              {!!estimateFeedback.relatedCaseIds?.length && (
                <div className="flex items-center gap-2">
                  <span className="shrink-0">Relaterade skador:</span>
                  {estimateFeedback.relatedCaseIds.map(relatedCaseId => (
                    <Link key={relatedCaseId} to={`/cases/${relatedCaseId}`}>
                      <Badge variant="secondary">{relatedCaseId}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4 text-sm">
            {summary && (
              <>
                <span>Fel: {summary.errors}</span>
                <span>Uppmaningar: {summary.warnings}</span>
                <span>Frågor: {summary.openQuestions}</span>
              </>
            )}
            {!!estimateFeedback.damages?.length && (
              <span className="flex items-center gap-2">
                Skador:
                {estimateFeedback.damages.map(damage => (
                  <Badge key={damage.scenario} variant="outline">
                    {positionTitle(damage.position)} ({DAMAGE_KIND_LABELS[damage.kind]})
                  </Badge>
                ))}
              </span>
            )}
          </CardContent>
        </div>

        {!estimateFeedback.damages?.length && findings.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground space-y-1">
            <p>Inga skadescenarion matchade den här kalkylen.</p>
            <p className="text-xs">Senast granskad {format(new Date(estimateFeedback.updatedAt), 'yyyy-MM-dd HH:mm')}</p>
          </div>
        ) : (
          <Tabs defaultValue="positions">
            <TabsList>
              <TabsTrigger value="positions">
                <TabLabel label="Positioner" findings={positionFindings} />
              </TabsTrigger>
              <TabsTrigger value="other">
                <TabLabel label="Övrigt" findings={otherFindings} />
              </TabsTrigger>
              <TabsTrigger value="other-paint">
                <TabLabel label="Övrig lack" findings={otherPaintFindings} />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="positions">
              <Card>
                <PositionFeedbackTable positions={positions} findings={findings} {...respondProps} />
              </Card>
            </TabsContent>

            <TabsContent value="other">
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <div className="divide-y">
                    {otherFindings.map(finding => (
                      <FindingItem key={finding.key} finding={finding} {...respondProps} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="other-paint">
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <div className="divide-y">
                    {otherPaintFindings.map(finding => (
                      <FindingItem key={finding.key} finding={finding} {...respondProps} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AppLayout>
  )
}

export default EstimateFeedback
